import {
  ArrowBack as ArrowBackIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Container,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import HardwareIdSubmission from "../components/user/HardwareIdSubmission";
import { useAuth } from "../contexts/AuthContext";
import { userProductService } from "../services/userProductService";
import { isLicenseServerAssignment } from "../utils/licenseServerUtils";

import { useNavigate } from "react-router-dom";

const HardwareIdManagementPage = () => {
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    loadMyProducts();
  }, []);

  const loadMyProducts = async () => {
    try {
      setLoading(true);
      const response = await userProductService.getMyProducts();
      setMyProducts(response.products || []);
      setError(null);
    } catch (err) {
      console.error("Error loading my products:", err);
      setError("Failed to load your assigned products");
    } finally {
      setLoading(false);
    }
  };

  const licensedProducts = myProducts.filter(
    (product) =>
      product.requires_license && !isLicenseServerAssignment(product),
  );

  const steps = [
    {
      label: "Products Assigned",
      description:
        "You have been assigned licensed products that require hardware IDs",
      icon: <AssignmentIcon />,
    },
    {
      label: "Submit Hardware IDs",
      description: "Submit your hardware ID for each licensed product",
      icon: <ComputerIcon />,
    },
    {
      label: "Admin Confirmation",
      description: "Wait for admin to confirm your hardware IDs",
      icon: <CheckCircleIcon />,
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            mr: { sm: 2 },
            mb: { xs: 1, sm: 0 },
            alignSelf: { xs: "flex-start", sm: "center" },
          }}
          size={window.innerWidth < 600 ? "small" : "medium"}
        >
          Back
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ p: 4, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <ComputerIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" component="h1">
            License Management
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Submit and manage hardware IDs for your licensed products. Hardware
          IDs are required before licenses can be generated for your products.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Process Steps */}
      <Card
        elevation={0}
        variant="outlined"
        sx={{
          mb: 4,
          background: "transparent",
        }}
      >
        <CardHeader
          title="Hardware ID Submission Process"
          subheader="Follow these steps to get your hardware IDs confirmed"
        />
        <CardContent>
          <Stepper orientation="horizontal" alternativeLabel>
            {steps.map((step, index) => (
              <Step key={index} active={index === 1}>
                <StepLabel icon={step.icon}>
                  <Typography variant="subtitle2">{step.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Main Content */}
      {licensedProducts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            No Licensed Products Assigned
          </Typography>
          <Typography>
            You don't have any products assigned that require hardware ID
            submission. Contact your administrator if you believe this is an
            error.
          </Typography>
        </Alert>
      ) : (
        <Box container spacing={4}>
          <Box sx={{ p: 4, mb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0 }}>
              <ComputerIcon color="primary" />
              <Typography variant="h6">
                Submit Hardware IDs ({licensedProducts.length} products)
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Submit hardware IDs for your licensed products. Once submitted and
              confirmed by admin, licenses can be generated.
            </Typography>
          </Box>

          <HardwareIdSubmission products={licensedProducts} />
        </Box>
      )}

      {/* Help Section */}
      <Card
        elevation={0}
        sx={{ mt: 4, bgcolor: "transparent", border: "none" }}
      >
        <CardHeader title="Need Help?" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                What is a Hardware ID?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A hardware ID is a unique identifier generated by your device or
                application. It's typically a base64-encoded string that helps
                identify your specific machine.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Why do I need to submit it?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hardware IDs ensure that licenses are tied to specific devices
                for security. Once confirmed by an admin, licenses can be
                generated for your products.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Can I change my Hardware ID?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can edit your hardware ID until it's confirmed by an admin.
                Once confirmed, only administrators can make changes.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                What happens after submission?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                After submission, an administrator will review and confirm your
                hardware ID. You'll be notified when it's approved and ready for
                license generation.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default HardwareIdManagementPage;
